import _ from 'underscore';

import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';

import * as codons from './Codons';
import * as util from './Util';

export interface ORFMatch {
  strand: StrandOfDoubleStranded;
  startCodon: codons.StandardDNACodon;
  endPosition: number | null;
  startPositions: Array<number>;
}

interface SequenceSearchOptions {
  regex: RegExp;
  onStartCodon: (pos: number) => void;
  onStopCodon: (pos: number) => void;
  transform: (pos: number) => number;
}

/**
 * Helper method to process all start/stop codons in a sequence starting from the first base,
 * including the two wrapping edge cases if circular.
 * @param  {RegExp} options.regex Regex for start/stop codons where the first matching group
 *                                should correspond to the start codon.
 * @param  {Function} options.onStartCodon Function that is passed the position at which the
 *                                         start codon was found.
 * @param  {Function} options.onStopCodon  Function that is passed the position at which the
 *                                         stop codon was found.
 */
function sequenceSearch(sequence: string, circular: boolean, options: SequenceSearchOptions): void {
  let match;
  let position;
  const {regex, onStartCodon, onStopCodon, transform} = options;

  // oxlint-disable-next-line no-cond-assign
  while ((match = regex.exec(sequence)) != null) {
    const index = regex.lastIndex - 3;
    regex.lastIndex = index + 1;
    position = transform(index);
    if (match[1] != null) {
      onStartCodon(position);
    } else {
      onStopCodon(position);
    }
  }
  if (circular && sequence.length >= 3) {
    [
      [sequence.length - 2, sequence.slice(-2) + sequence.slice(0, 1)],
      [sequence.length - 1, sequence.slice(-1) + sequence.slice(0, 2)],
    ].forEach((...args) => {
      const [index, codon] = Array.from(args[0]);
      // oxlint-disable-next-line no-cond-assign
      if ((match = regex.exec(codon as string)) != null) {
        position = transform(index as number);
        if (match[1] != null) {
          onStartCodon(position);
        } else {
          onStopCodon(position);
        }
      }
    });
  }
}

/**
 * The functions below find the ORFs on a sequence for a particular startCodon.
 * @param  {String} sequence   the string of bases to search over
 * @param  {Boolean} circular  whether or not we should include wrapping results
 * @param  {String} startCodon 3-letter string, expected one of ['ATG', 'TTG', 'CTG', 'GTG']
 * @return {Array}             the matches found where each match is of the form:
 *                                 strand          1 for forward straned, -1 for reverse
 *                                 startCodon      caller-specified startCodon
 *                                 endPosition     the index of the 5' end of the stop codon
 *                                 startPositions  an array of the 5' ends of the start codons,
 *                                                 sorted desc. by distance from stop codon
 */
export function forwardStrandOrfsWithStartCodon(
  sequence: string,
  circular: boolean,
  startCodon: codons.StandardDNACodon
): Array<ORFMatch> {
  let offset: number;
  const results: Array<ORFMatch> = [];
  // FWD STRAND: Track startPositions by frame, and then create ORFs as we find endPositions in the same frame

  // lookup for startPositions by frame without matching endPosition yet
  const openOrfs: Array<Array<number>> = [[], [], []];
  // lookup by frame for the first endPosition in each frame
  const firstStops: Array<number | null> = [null, null, null];
  const orfsByEnd: {[pos: number]: ORFMatch} = {}; // lookup ORFs keyed on endPosition
  sequenceSearch(sequence, circular, {
    regex: new RegExp(`(${startCodon})|(${codons.DNA_STOP_CODONS.join('|')})`, 'gi'),
    transform(index: number): number {
      return index;
    },

    // 1. Keep track of start positions keyed on the reading frame (openOrfs) as we encounter them.
    onStartCodon(position: number): void {
      const frame = position % 3;
      openOrfs[frame].push(position);
    },

    // 2. As we encounter a stop codon, create an ORF with all of the start codons in the same frame, and then
    //     reset the array in openOrfs.
    onStopCodon(position: number): void {
      const frame = position % 3;
      if (firstStops[frame] == null) {
        firstStops[frame] = position;
      }
      if (openOrfs[frame].length) {
        orfsByEnd[position] = {strand: 1, startCodon, endPosition: position, startPositions: openOrfs[frame]};
        openOrfs[frame] = [];
        results.push(orfsByEnd[position]);
      }
    },
  });
  // 3. Close any open ORFs.
  if (circular) {
    // If the sequence is circular, we can wrap around the sequence to the first stop codon in the same frame,
    // so long as the stop codon does not overlap the start codon. We only have to consider the first codon
    // for each frame. We only include startPositions that start after the target stop codon.
    offset = sequence.length % 3;
    firstStops.forEach((endPosition, endFrame) => {
      let startPositions = openOrfs[(endFrame + offset) % 3];
      if (endPosition == null) {
        return;
      }
      // Because startPositions is sorted, _.sortedIndex for endPosition + 3 (the smallest valid start
      //  position), will give the lowest index into startPositions where startPosition >= endPosition + 3.
      const firstValidStartPositionIndex = _.sortedIndex(startPositions, endPosition + 3);
      startPositions = startPositions.slice(firstValidStartPositionIndex);
      if (!startPositions.length) {
        return;
      }
      // Create the ORF if there is no ORF with the desired endPosition
      if (orfsByEnd[endPosition] == null) {
        orfsByEnd[endPosition] = {strand: 1, startCodon, endPosition, startPositions: []};
        results.push(orfsByEnd[endPosition]);
      }
      // Add the remaining startPositions to the beginning of the ORF's startPositions, as the remaining
      // startPositions are the "leftmost" ones, e.g. [->4-->5-->X 1-->2-->3-], [4,5,X] is the existing
      // ORF, and [1,2,3] are left.
      orfsByEnd[endPosition].startPositions.splice(0, 0, ...Array.from(startPositions));
    });
  } else {
    // If the sequence is linear, create an ORF for the startPositions in each frame.
    openOrfs.forEach((startPositions, startFrame) => {
      if (startPositions.length) {
        results.push({strand: 1, startCodon, endPosition: null, startPositions});
      }
    });
  }
  return results;
}

export function reverseStrandOrfsWithStartCodon(
  sequence: string,
  circular: boolean,
  startCodon: codons.StandardDNACodon
): Array<ORFMatch> {
  let offset: number;
  const results: Array<ORFMatch> = [];
  // REV STRAND: Track last endPosition by frame; create/update ORFs for startPositions in the same frame

  const openOrfs: Array<Array<number>> = [[], [], []]; // lookup for startPositions by frame without matching endPosition yet
  // lookup by frame for the first endPosition in each frame
  const lastStops: Array<number | null> = [null, null, null];
  const orfsByEnd: {[pos: number]: ORFMatch} = {}; // lookup ORFs keyed on endPosition

  sequenceSearch(sequence, circular, {
    // We search for revcomps of codons instead of searching over revcomp of sequence (slow to compute)
    regex: new RegExp(
      `(${util.revcomp(startCodon, DNA_COMPLEMENT_TABLE)})|(${codons.DNA_STOP_CODONS.map((codon) =>
        util.revcomp(codon, DNA_COMPLEMENT_TABLE)
      ).join('|')})`,
      'gi'
    ),
    transform(index: number): number {
      return (index + 2) % sequence.length;
    }, // Indices refer to the 5' end of the codon

    // 1. Keep track of the last codon in each frame.
    onStopCodon(position: number): void {
      const frame = position % 3;
      lastStops[frame] = position;
    },

    // 2. Create/update the ORF that has an endPosition in the same frame as the current startPosition.
    onStartCodon(position: number): void {
      const frame = position % 3;
      const endPosition = lastStops[frame];
      if (endPosition != null) {
        if (orfsByEnd[endPosition] == null) {
          orfsByEnd[endPosition] = {strand: -1, startCodon, endPosition, startPositions: []};
          results.push(orfsByEnd[endPosition]);
        }
        orfsByEnd[endPosition].startPositions.push(position);
      } else {
        openOrfs[frame].push(position);
      }
    },
  });
  // 3. Close any open ORFs.
  if (circular) {
    // If the sequence is circular, we can wrap around the sequence to the last stop codon in the same frame,
    // so long as the stop codon does not overlap the start codon. We only have to consider the last stop
    // codon for each frame. We only include startPositions that start before the target stop codon.
    offset = sequence.length % 3;
    openOrfs.forEach((startPositions, startFrame) => {
      const endPosition = lastStops[(startFrame + offset) % 3];
      if (endPosition == null) {
        return;
      }
      // Because startPositions is sorted, _.sortedIndex for endPosition - 2 (the smallest invalid start
      // position), will give the lowest index into startPositions where startPosition >= endPosition - 2,
      // meaning all previous indices will have startPosition <= endPosition - 3
      const lastValidStartPositionIndex = _.sortedIndex(startPositions, endPosition - 2);
      startPositions = startPositions.slice(0, lastValidStartPositionIndex);
      if (!startPositions.length) {
        return;
      }
      // Create the ORF if there is no ORF with the desired endPosition
      if (orfsByEnd[endPosition] == null) {
        orfsByEnd[endPosition] = {strand: -1, startCodon, endPosition, startPositions: []};
        results.push(orfsByEnd[endPosition]);
      }
      // Add the remaining startPositions to the end of the ORF's startPositions, as the remaining
      // startPositions are the "rightmost" ones, e.g. [-4<--5  X<--1<--2<--3<-], [1,2,3] is the existing
      // ORF, and [4,5] are left.
      orfsByEnd[endPosition].startPositions.push(...Array.from(startPositions || []));
    });
  } else {
    // If the sequence is linear, create an ORF for the startPositions in each frame.
    openOrfs.forEach((startPositions, startFrame) => {
      if (startPositions.length) {
        results.push({strand: -1, startCodon, endPosition: null, startPositions});
      }
    });
  }
  return results;
}
